<template>
  <gs-card class="gs-library-header">
    <div class="gs-library-header__top">
      <div class="gs-library-header__intro">
        <span class="gs-library-header__eyebrow">GrooveVideo</span>
        <h1 class="gs-library-header__title">Your video library</h1>
        <p class="gs-library-header__subtitle">
          Paste a YouTube link, brand the player, add calls to action, then embed it anywhere.
        </p>
        <p class="gs-library-header__facts">
          {{ total }} videos<template v-if="published !== null"> &middot; {{ published }} published</template>
        </p>
      </div>
      <gs-button variant="primary" @click="$emit('add')">
        <gs-icon name="plus" size="16" />
        Add video
      </gs-button>
    </div>

    <div class="gs-library-header__filters">
      <gs-field class="gs-library-header__search-field">
        <div class="gs-library-header__search">
          <gs-icon name="search" size="15" class="gs-library-header__search-icon" />
          <input
            type="search"
            class="gs-library-header__search-input"
            :value="localSearch"
            placeholder="Search videos"
            aria-label="Search videos"
            @input="onSearchInput($event.target.value)"
          />
        </div>
      </gs-field>

      <treeselect
        class="gs-library-header__category"
        :value="category"
        :multiple="false"
        :options="categoryOptions"
        :normalizer="normalizer"
        :close-on-select="true"
        :clearable="false"
        :clear-on-select="true"
        :is-default-expanded="true"
        :default-expand-level="1"
        :show-count="true"
        :max-height="280"
        placeholder="All categories"
        @input="$emit('update:category', $event)"
      />

      <gd-dropdown
        class="gs-library-header__order"
        :options="orderOptions"
        :value="order"
        size="full"
        @change="$emit('update:order', $event)"
      />
    </div>
  </gs-card>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';

export default {
  name: 'LibraryHeader',
  components: { Treeselect },
  props: {
    total: {
      type: Number,
      default: 0,
    },
    published: {
      type: Number,
      default: null,
    },
    categoryOptions: {
      type: Array,
      default: () => [],
    },
    category: {
      type: Number,
      default: 0,
    },
    order: {
      type: String,
      default: '',
    },
    orderOptions: {
      type: Array,
      default: () => [],
    },
    search: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      localSearch: this.search,
      searchTimer: null,
    };
  },
  watch: {
    search(value) {
      this.localSearch = value;
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    normalizer(node) {
      return {
        id: node.id,
        label: node.category,
        children: node.child && node.child.length ? node.child : undefined,
        user_id: node.user_id,
      };
    },
    onSearchInput(value) {
      this.localSearch = value;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.$emit('update:search', value);
      }, 500);
    },
  },
};
</script>

<style scoped>
.gs-library-header {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px 28px;
}

.gs-library-header__top {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  justify-content: space-between;
}

.gs-library-header__intro {
  min-width: 0;
}

.gs-library-header__eyebrow {
  display: block;
  margin-bottom: 6px;
  color: var(--gs-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-library-header__title {
  margin: 0 0 6px;
  color: var(--gs-foreground);
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

.gs-library-header__subtitle {
  margin: 0 0 10px;
  max-width: 60ch;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.55;
}

.gs-library-header__facts {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.gs-library-header__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.gs-library-header__search-field {
  flex: 1 1 240px;
  min-width: 220px;
}

.gs-library-header__search {
  display: flex;
  gap: 8px;
  align-items: center;
  min-height: 40px;
  padding: 0 12px;
  background: var(--gs-input);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-library-header__search:focus-within {
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-library-header__search-icon {
  flex: none;
  color: var(--gs-muted-foreground);
}

.gs-library-header__search-input {
  flex: 1 1 auto;
  min-width: 0;
  color: var(--gs-foreground);
  background: transparent;
  border: 0;
  font-size: 14px;
}

.gs-library-header__search-input:focus {
  outline: none;
}

.gs-library-header__category,
.gs-library-header__order {
  flex: none;
  width: 220px;
}

.gs-library-header__category ::v-deep .vue-treeselect__control {
  min-height: 40px;
  border-radius: var(--gs-radius-sm);
}

.gs-library-header__order ::v-deep .relative.h-8 {
  height: 40px;
  border-radius: var(--gs-radius-sm);
}
</style>
